
import { useAuthStore } from "@/lib/store"
import { apiClient } from "@/utils/api"
import { parseJwtToken } from "@/utils/auth"
import { ICompleteChallengeResponse, IRootAdminSessionData, ITenantUserSessionData } from "@portafino/shared"
import { useCallback, useEffect, useMemo } from "react"
import { useConnect } from "wagmi"
import { ChallengeProps, CompleteChallengeProps, useGianoConnector } from "./use-giano-connector"
import { updateBundlerAuth } from "@/config/wagmi"
import { useSentry } from "./use-sentry"
import { isExpectedAuthError } from "@/utils/error"

interface OnConnectProps {
    onSuccess?: () => void
    onError?: (error: unknown) => void
}

interface Props {
    challenge: ChallengeProps
    completeChallenge: CompleteChallengeProps
}

export const useGianoConnect = (props: Props) => {
    const { challenge: challengeProps, completeChallenge: providedCompleteChallengeProps } = props

    const { connect, isError: isErrorConnecting, isSuccess: isSuccessConnecting, isPending: isConnecting } = useConnect()

    const loginTenantUser = useAuthStore((state) => state.loginTenantUser);
    const loginRootAdminUser = useAuthStore((state) => state.loginRootAdminUser);
    const user = useAuthStore((state) => state.user)

    const { refetch: refetchChallenge } = challengeProps

    const { captureException, setUser: setSentryUser } = useSentry()

    const onChallengeCompleted = useCallback(async (data: ICompleteChallengeResponse) => {
        try {
            if ('clientAccessToken' in data && data.clientAccessToken) {
                // Take the access token after completing the challenge
                // and use it in the next API requests
                const { clientAccessToken } = data
                const decodedToken = parseJwtToken<ITenantUserSessionData | IRootAdminSessionData>(clientAccessToken)
                apiClient.setBearerToken(clientAccessToken)
                const isTenantUser = 'tenantUserId' in decodedToken
                if (isTenantUser) {
                    const userId = decodedToken.tenantUserId.toString()
                    const tenantId = decodedToken.tenantId.toString()
                    await loginTenantUser(decodedToken.credentialId, userId, tenantId, clientAccessToken)
                } else {
                    const userId = decodedToken.rootAdminId.toString()
                    await loginRootAdminUser(decodedToken.credentialId, userId, clientAccessToken)
                }
                updateBundlerAuth(clientAccessToken)
            }
        } catch (error) {
            // Refetch a new challenge so we are ready to try logging in again
            refetchChallenge?.()
            throw error
        }
    }, [loginTenantUser, loginRootAdminUser, refetchChallenge])

    const completeChallengeProps = useMemo(() => ({
        ...providedCompleteChallengeProps,
        onSuccess: onChallengeCompleted
    }), [providedCompleteChallengeProps, onChallengeCompleted])

    const {
        challenge,
        gianoConnector, 
        errorFetchingChallenge, 
        isLoadingChallenge, 
        errorCompletingChallenge, 
        isPendingCompletingChallenge
    } = useGianoConnector({ action: 'signin', challenge: challengeProps, completeChallenge: completeChallengeProps })

    useEffect(() => {
        if (user) {
            setSentryUser(user)
        }
    }, [user, setSentryUser])

    const connectGiano = useCallback(async (props: OnConnectProps = {}) => {
        connect({ connector: gianoConnector }, {
            onError: (error) => {
                // A wrong or dismissed passkey, or a device that cannot reach its passkey service,
                // is the person's situation rather than a fault of ours, and the caller tells them.
                // beforeSend drops these too, but reporting from here would still cost a round trip
                // and bury the genuine failures.
                if (!isExpectedAuthError(error)) {
                    captureException(error)
                }
                props.onError?.(error)
            },
            onSuccess: () => {
                props.onSuccess?.()
            }
        })
        
    }, [connect, gianoConnector, captureException])

    

    const isReadyToConnect = Boolean(challenge) && !isLoadingChallenge

    return {
        isReadyToConnect,
        connect: connectGiano,
        errorFetchingChallenge,
        isLoadingChallenge,
        errorCompletingChallenge,
        isPendingCompletingChallenge,
        refetchChallenge,
        isErrorConnecting,
        isSuccessConnecting,
        isConnecting,
    }
}
